import React, { ReactElement, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProfileViewModeBg from "../../assets/images&icons/bg-login.jpg";
import NewButton from "../../components/Atomos/Buttons/newButton";
import Toggle from "../../components/Atomos/Checkbox/Toggle";
import AuthContext from "../../store/AuthContext";
import { patchUserPreferences } from "../../Services/UserPreferences";
import EventBus, { EventBusValues } from "../../utils/EventBus";
import { setCookie } from "../../utils/cookies";
import { getAndClearRedirectPath } from "../../utils/logout";

function TwoFactorAcknowledgement(): ReactElement {
  const [twoAuthEnabled, setTwoAuthEnabled] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const { loggedPerson, set2FAuth } = useContext(AuthContext);

  useEffect(function validateTwoFactorAuthentication() {
    if (!loggedPerson) {
      return;
    }

    if (loggedPerson.twoFactorAuthentication) {
      const redirectPath = getAndClearRedirectPath();
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/mywsg', { replace: true });
      }
    }
  }, [loggedPerson, navigate]);

  const handleSubmit = async () => {
    if (!loggedPerson || isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const twoFactorAuthentication = {
        status: twoAuthEnabled,
        updatedAt: new Date().toISOString(),
      };
      await patchUserPreferences({ twoFactorAuthentication });
      const updatedPerson = {
        ...loggedPerson,
        twoFactorAuthentication,
      };
      EventBus.dispatch(EventBusValues.UpdateLoggedPerson, updatedPerson);
      
      if (twoAuthEnabled) {
        // User enabled 2FA, they need to complete 2FA verification
        // Store the necessary data for 2FA completion
        const pendingLoginData = {
          username: loggedPerson.firmEmail || loggedPerson.userId, // Use email or user ID for 2FA
          fromAcknowledgement: true,
          shouldProceedAfter2FA: true
        };
        sessionStorage.setItem("pendingLogin", JSON.stringify(pendingLoginData));
        set2FAuth(false); // Reset 2FA status so they need to verify
        navigate('/two-factor-auth', { replace: true });
      } else {
        // User disabled 2FA, proceed with normal login flow
        setCookie("2FAuth", "true", 0); // They don't need 2FA
        set2FAuth(true);
        
        const redirectPath = getAndClearRedirectPath();
        if (redirectPath) {
          navigate(redirectPath, { replace: true });
        } else {
          navigate('/mywsg', { replace: true });
        }
      }
    } catch (error) {
      console.error('Error updating 2FA setting:', error);
      // Handle error - maybe show an error message
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <>
      <div
        className="absolute w-full min-h-screen"
        style={{
          backgroundImage: `url(${ProfileViewModeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      ></div>
      <div className="w-full min-h-screen justify-center items-center flex self-stretch relative px-4 sm:px-6 lg:px-8 -top-20">
        <div className="w-full max-w-md sm:max-w-lg lg:max-w-[520px] mx-auto">
          <div
            className={`flex w-full align-middle justify-center px-4 sm:px-6 py-4 bg-[#9C3325]`}
          >
            <h2
              className={`text-center text-lg sm:text-xl lg:text-2xl font-sans text-white roboto-font leading-tight`}
            >
              TWO-FACTOR AUTHENTICATION
            </h2>
          </div>
          <div className="bg-white w-full py-4 sm:py-6">
            <div className="flex flex-col gap-y-4 px-4 sm:px-6">
              <h2 className={`text-left text-xl sm:text-2xl font-sans roboto-font text-[#474f56] font-bold smooth`}>
                Action Required
              </h2>
              <div className="w-full min-h-16">
                <h5 className="roboto-font py-2 w-full text-sm sm:text-base text-[#474f56] smooth font-normal leading-relaxed text-left">
                  Activate Two-Factor Authentication. This improves account
                  security and protects the WSG Community.
                </h5>
              </div>
              <div className="flex flex-row items-center gap-x-4 flex-wrap">
                <span className="text-base sm:text-lg font-sans roboto-font text-[#474f56] font-bold leading-tight flex-1 min-w-0 text-left">
                  Enable Two-Factor Authentication via Email
                </span>
                <div className="flex-shrink-0">
                  <Toggle toggleEnabled={twoAuthEnabled} handleToggle={() => setTwoAuthEnabled(!twoAuthEnabled)} />
                </div>
              </div>

              <div className="pt-2">
                <NewButton 
                  text={isSubmitting ? "Logging in..." : "Login"} 
                  type="submit" 
                  onClick={() => handleSubmit()}
                  disabled={isSubmitting}
                />
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TwoFactorAcknowledgement;
